import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    apiGetPrescriptions,
    apiGetPrescriptionDetail,
    apiCreatePrescription,
    apiEditPrescription,
    apiDeletePrescription
} from '../api/prescriptions/prescriptions';
import { handleApiError } from '../lib/errorHandler';
import { TPrescription, TPrescriptionsList } from '../context/PrescriptionProvider';

interface PrescriptionListOptions {
    search?: string;
}

export function usePrescriptionOperations() {
    const { t } = useTranslation();
    const [prescriptionsList, setPrescriptionsList] = useState<TPrescriptionsList>({
        count: 0,
        results: [],
    });
    const [prescriptionDetail, setPrescriptionDetail] = useState<TPrescription | null>(null);
    const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);

    const getPrescriptionList = async (
        options: PrescriptionListOptions,
        limit: number,
        offset: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        setPrescriptionsLoading(true);
        try {
            const response = await apiGetPrescriptions({
                limit,
                offset,
                search: options.search,
            });

            if (response.data) {
                setPrescriptionsList(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.prescriptions.loadingList'), 'prescriptions');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        } finally {
            setPrescriptionsLoading(false);
        }
    };

    const getPrescriptionDetail = async (
        id: string,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await apiGetPrescriptionDetail(id);
            if (response.data) {
                setPrescriptionDetail(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.prescriptions.loadingDetail'), 'prescriptions');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const createPrescription = async (
        data: any,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await apiCreatePrescription(data);
            if (response.data) {
                setAlertMessages({});
                return response.data;
            }
            return null;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.prescriptions.creating'), 'prescriptions');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
            return null;
        }
    };

    const editPrescription = async (
        id: number,
        data: any,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await apiEditPrescription(id, data);
            if (response.data) {
                setAlertMessages({});
                return response.data;
            }
            return null;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.prescriptions.updating'), 'prescriptions');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
            return null;
        }
    };

    const deletePrescription = async (
        id: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            await apiDeletePrescription(id);
            setAlertMessages({});
            return true;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.prescriptions.deleting'), 'prescriptions');
            setAlertMessages(serverError as Partial<Record<string, string[]>>);
            return false;
        }
    };

    return {
        prescriptionsList,
        prescriptionDetail,
        prescriptionsLoading,
        getPrescriptionList,
        getPrescriptionDetail,
        createPrescription,
        editPrescription,
        deletePrescription,
    };
}
